import APIServices from 'services/APIServices';

const OAUTH_POPUP_NAME = 'google-calendar-oauth';
const OAUTH_POPUP_FEATURES = 'popup=yes,width=520,height=680';

const getResponseData = (response) => response?.data || response;

const requestBackendAuthorization = async () => {
  const popup = window.open('', OAUTH_POPUP_NAME, OAUTH_POPUP_FEATURES);
  if (!popup) {
    throw new Error('Allow popups for this site to connect Google Calendar.');
  }

  try {
    const response = await APIServices.post('/google-calendar/auth-url', {
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
    });
    const data = getResponseData(response);

    if (response?.status >= 400 || !data?.authorizationUrl) {
      throw new Error(data?.message || 'Unable to start Google Calendar authorization.');
    }

    popup.location.href = data.authorizationUrl;

    await new Promise((resolve, reject) => {
      const timeoutId = window.setTimeout(() => {
        cleanup();
        reject(new Error('Google Calendar authorization timed out.'));
      }, 5 * 60 * 1000);
      const pollId = window.setInterval(() => {
        if (popup.closed) {
          cleanup();
          reject(new Error('Google Calendar authorization was cancelled.'));
        }
      }, 500);

      const cleanup = () => {
        window.clearTimeout(timeoutId);
        window.clearInterval(pollId);
        window.removeEventListener('message', handleMessage);
      };

      const handleMessage = (event) => {
        if (event.source !== popup) return;
        if (event.data?.type !== 'google-calendar-oauth') return;

        cleanup();
        if (event.data.success) {
          resolve();
        } else {
          reject(new Error(event.data.message || 'Google Calendar authorization failed.'));
        }
      };

      window.addEventListener('message', handleMessage);
    });
  } catch (error) {
    if (!popup.closed) popup.close();
    throw error;
  }
};

const exportFromBackend = async () => {
  const response = await APIServices.post('/google-calendar/export', {
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
  });
  const data = getResponseData(response);

  if (response?.status >= 400) {
    const error = new Error(data?.message || 'Unable to export tasks to Google Calendar.');
    error.code = data?.code;
    throw error;
  }

  return data;
};

export const prepareGoogleCalendarExport = () => Promise.resolve();

export const disconnectGoogleCalendar = async () => {
  const response = await APIServices.delete('/google-calendar/disconnect');
  const data = getResponseData(response);

  if (response?.status >= 400) {
    throw new Error(data?.message || 'Unable to disconnect Google Calendar.');
  }

  return data;
};

export const exportEventsToGoogleCalendar = async ({ connected = false } = {}) => {
  if (!connected) {
    await requestBackendAuthorization();
  }

  try {
    return await exportFromBackend();
  } catch (error) {
    if (error.code !== 'GOOGLE_CALENDAR_NOT_CONNECTED') throw error;

    await requestBackendAuthorization();
    return exportFromBackend();
  }
};
